import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  getCourse,
  updateCourse,
  newCategory,
  newMainTitle,
  newSubTitle,
  countLessons,
} from '../../utils/studyMaterialsApi';

const LANGUAGES = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'ta', flag: '🇮🇳', label: 'Tamil' },
];

const LESSON_STATUSES = ['draft', 'published'];

function fmtSavedAt(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(iso));
}

// ── Rich Text Editor (kept from the previous editor implementation) ────────
const ToolbarBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
  >
    {children}
  </button>
);

const ToolbarSep = () => <div className="mx-0.5 h-5 w-px bg-slate-200" />;

const RichEditor = React.forwardRef(function RichEditor({ defaultValue, onChange, placeholder, editorKey }, forwardedRef) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = defaultValue || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  React.useImperativeHandle(forwardedRef, () => ({
    clear: () => {
      if (!ref.current) return;
      ref.current.innerHTML = '';
      ref.current.focus();
      onChange('');
    },
  }));

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(ref.current?.innerHTML || '');
  };

  const block = (tag) => exec('formatBlock', tag);

  const insertLink = () => {
    const url = prompt('Enter link URL:', 'https://');
    if (url) exec('createLink', url);
  };

  const insertCode = () => {
    ref.current?.focus();
    document.execCommand('insertHTML', false,
      '<pre style="background:#f1f5f9;border-radius:6px;padding:12px;font-family:monospace;white-space:pre-wrap;"><code>// code here</code></pre>'
    );
    onChange(ref.current?.innerHTML || '');
  };

  const insertHR = () => {
    ref.current?.focus();
    document.execCommand('insertHTML', false, '<hr style="border:none;border-top:2px solid #e2e8f0;margin:16px 0;" />');
    onChange(ref.current?.innerHTML || '');
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) exec('insertImage', url);
  };

  const insertVideo = () => {
    const raw = prompt('Enter YouTube video URL:');
    if (!raw) return;
    const m = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
    const id = m?.[1];
    if (!id) { alert('Could not detect YouTube video ID.'); return; }
    ref.current?.focus();
    document.execCommand('insertHTML', false,
      `<div style="position:relative;padding-top:56.25%;margin:12px 0;">
        <iframe src="https://www.youtube.com/embed/${id}" style="position:absolute;inset:0;width:100%;height:100%;border:0;" allowfullscreen></iframe>
       </div>`
    );
    onChange(ref.current?.innerHTML || '');
  };

  const insertTable = () => {
    ref.current?.focus();
    document.execCommand('insertHTML', false,
      `<table style="border-collapse:collapse;width:100%;margin:8px 0;">
        <tbody>
          <tr><td style="border:1px solid #cbd5e1;padding:6px;">Cell</td><td style="border:1px solid #cbd5e1;padding:6px;">Cell</td></tr>
          <tr><td style="border:1px solid #cbd5e1;padding:6px;">Cell</td><td style="border:1px solid #cbd5e1;padding:6px;">Cell</td></tr>
        </tbody>
       </table>`
    );
    onChange(ref.current?.innerHTML || '');
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100 transition">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <ToolbarBtn onClick={() => block('h1')} title="Heading 1"><span className="font-black">H1</span></ToolbarBtn>
        <ToolbarBtn onClick={() => block('h2')} title="Heading 2"><span className="font-black">H2</span></ToolbarBtn>
        <ToolbarBtn onClick={() => block('h3')} title="Heading 3"><span className="font-black">H3</span></ToolbarBtn>
        <ToolbarBtn onClick={() => block('h4')} title="Heading 4"><span className="font-black">H4</span></ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={() => exec('bold')} title="Bold"><strong>B</strong></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Italic"><em>I</em></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Underline"><u>U</u></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('strikeThrough')} title="Strikethrough"><s>S</s></ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Numbered list">1.</ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list">••</ToolbarBtn>
        <ToolbarBtn onClick={() => block('blockquote')} title="Quote">"</ToolbarBtn>
        <ToolbarBtn onClick={insertHR} title="Horizontal rule">—</ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={insertLink} title="Insert link">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
          </svg>
        </ToolbarBtn>
        <ToolbarBtn onClick={insertCode} title="Code block">&lt;/&gt;</ToolbarBtn>
        <ToolbarBtn onClick={insertImage} title="Insert image">🖼️</ToolbarBtn>
        <ToolbarBtn onClick={insertVideo} title="Insert video">🎥</ToolbarBtn>
        <ToolbarBtn onClick={insertTable} title="Insert table">▦</ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={() => exec('undo')} title="Undo">↺</ToolbarBtn>
        <ToolbarBtn onClick={() => exec('redo')} title="Redo">↻</ToolbarBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        data-ph={placeholder || 'Start writing content here…'}
        className="rich-content min-h-[260px] cursor-text px-4 py-3 text-sm text-slate-800 outline-none"
        style={{ lineHeight: 1.75 }}
      />
      <style>{`
        .rich-content:empty:before { content: attr(data-ph); color:#94a3b8; pointer-events:none; display:block; }
        .rich-content h1 { font-size:1.5rem; font-weight:700; margin:12px 0 6px; }
        .rich-content h2 { font-size:1.25rem; font-weight:700; margin:10px 0 5px; }
        .rich-content h3 { font-size:1.1rem; font-weight:600; margin:8px 0 4px; }
        .rich-content h4 { font-size:1rem; font-weight:600; margin:6px 0 3px; }
        .rich-content p  { margin:4px 0; }
        .rich-content blockquote { border-left:4px solid #8b5cf6; padding:6px 12px; margin:8px 0; background:#f5f3ff; border-radius:0 6px 6px 0; color:#6d28d9; }
        .rich-content ul { list-style:disc; padding-left:20px; margin:6px 0; }
        .rich-content ol { list-style:decimal; padding-left:20px; margin:6px 0; }
        .rich-content a  { color:#7c3aed; text-decoration:underline; }
        .rich-content pre { background:#f1f5f9; border-radius:6px; padding:12px; font-family:monospace; white-space:pre-wrap; margin:8px 0; }
        .rich-content img { max-width:100%; border-radius:8px; margin:8px 0; }
        .rich-content table td { border:1px solid #cbd5e1; padding:6px; }
      `}</style>
    </div>
  );
});

// ── Attachment zone (prototype: metadata only, no real upload) ─────────────
const STATUS_DOT = { draft: 'bg-amber-400', published: 'bg-emerald-400' };
const STATUS_BADGE = {
  draft: 'bg-amber-100 text-amber-700',
  published: 'bg-emerald-100 text-emerald-700',
};

// ── Sidebar tree node components ────────────────────────────────────────────
function TreeSubTitle({ sub, selected, editMode, onSelect, onRename, onDelete, onMove, isFirst, isLast }) {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(sub.title);

  const commit = () => {
    setRenaming(false);
    if (val.trim() && val.trim() !== sub.title) onRename(val.trim());
  };

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg py-1.5 pl-8 pr-1.5 text-xs transition ${
        selected ? 'bg-violet-100 text-violet-800 font-semibold' : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {renaming ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(sub.title); setRenaming(false); } }}
          className="flex-1 min-w-0 rounded border border-violet-300 bg-white px-1.5 py-0.5 text-xs outline-none"
        />
      ) : (
        <button onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          <span className="truncate">{sub.title}</span>
        </button>
      )}
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${STATUS_BADGE[sub.status] || STATUS_BADGE.draft}`}>
        {sub.status || 'draft'}
      </span>
      {editMode && !renaming && (
        <div className="hidden shrink-0 items-center gap-0.5 group-hover:flex">
          <button onClick={() => onMove(-1)} disabled={isFirst} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" title="Move up">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg>
          </button>
          <button onClick={() => onMove(1)} disabled={isLast} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" title="Move down">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
          </button>
          <button onClick={() => setRenaming(true)} className="rounded p-0.5 text-slate-300 hover:text-violet-600" title="Rename">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 18.82 2.25 20.25l1.43-4.582 13.182-13.181z" /></svg>
          </button>
          <button onClick={onDelete} className="rounded p-0.5 text-slate-300 hover:text-red-500" title="Delete">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// Inline "type a name + Add" row used for creating categories/main titles/sub
// titles in place, instead of a browser prompt() dialog.
function AddRow({ placeholder, indentClass, onAdd, onCancel }) {
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = () => {
    if (!val.trim()) return;
    onAdd(val.trim());
  };

  return (
    <div className={`flex items-center gap-2 py-1 pr-2 ${indentClass || ''}`}>
      <input
        ref={inputRef}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => { if (!val.trim()) onCancel(); }}
        placeholder={placeholder}
        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
      />
      <button
        onClick={submit}
        disabled={!val.trim()}
        className="shrink-0 rounded-lg bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-300 disabled:opacity-50 transition"
      >
        Add
      </button>
    </div>
  );
}

// ── Quick Add via Excel ──────────────────────────────────────────────────────
// Bulk-editing the Category/Main Title/Sub Title structure by hand is slow
// for large courses, so this offers a flat spreadsheet round-trip instead:
// download the current (or a blank) table of content, edit it in Excel, and
// upload it back. Rows are matched by exact text at each level so existing
// lesson status/visibility/content survive for anything that still matches;
// unmatched rows become new lessons and anything left out gets removed.
const TOC_SHEET_NAME = 'Table of Content';
const TOC_COLUMNS = ['Category', 'Main Title', 'Sub Title'];

function flattenTreeToRows(tree) {
  const rows = [];
  (tree || []).forEach((cat) => {
    const mainTitles = cat.mainTitles || [];
    if (mainTitles.length === 0) { rows.push([cat.name, '', '']); return; }
    mainTitles.forEach((mt) => {
      const subTitles = mt.subTitles || [];
      if (subTitles.length === 0) { rows.push([cat.name, mt.title, '']); return; }
      subTitles.forEach((st) => rows.push([cat.name, mt.title, st.title]));
    });
  });
  return rows;
}

function buildInstructionsSheet() {
  const lines = [
    ['Study Materials — Table of Content Import'],
    [],
    ['Column', 'Meaning'],
    ['Category', 'Top-level grouping, e.g. "Getting Started"'],
    ['Main Title', 'Section under a category, e.g. "Computer Preparation"'],
    ['Sub Title', 'The actual lesson, e.g. "Install Python 3.13"'],
    [],
    ['Rules'],
    ['Repeat the Category / Main Title text on every row it applies to.'],
    ['Rows are matched by exact text at each level: matching names are updated in place, new names are added.'],
    ['Anything left out of the uploaded sheet is removed from the course — re-download "Current Data" first if you only want to change a few rows.'],
    ['Existing lesson status, visibility and content are preserved for matched Sub Titles; brand new Sub Titles start as Draft with empty content.'],
  ];
  return XLSX.utils.aoa_to_sheet(lines);
}

function downloadTocTemplate(tree, { blank }) {
  const rows = blank ? [] : flattenTreeToRows(tree);
  const worksheet = XLSX.utils.aoa_to_sheet([TOC_COLUMNS, ...rows]);
  worksheet['!cols'] = [{ wch: 28 }, { wch: 32 }, { wch: 40 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, TOC_SHEET_NAME);
  XLSX.utils.book_append_sheet(workbook, buildInstructionsSheet(), 'Instructions');
  const suffix = blank ? 'Blank_Template' : 'Current_Data';
  XLSX.writeFile(workbook, `Study_Material_ToC_${suffix}_${Date.now()}.xlsx`);
}

async function parseTocFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames.find((n) => n.trim().toLowerCase() !== 'instructions') || workbook.SheetNames[0];
  const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!worksheet) throw new Error('This workbook does not contain any sheets.');

  const matrix = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, blankrows: false });
  if (!matrix.length) throw new Error('The worksheet is empty.');

  const [header, ...dataRows] = matrix;
  const colIndex = (label) => header.findIndex((h) => String(h || '').trim().toLowerCase() === label.toLowerCase());
  const catCol = colIndex('Category');
  const mtCol = colIndex('Main Title');
  const stCol = colIndex('Sub Title');
  if (catCol === -1 || mtCol === -1 || stCol === -1) {
    throw new Error('Expected columns "Category", "Main Title" and "Sub Title" were not found.');
  }

  return dataRows
    .map((row) => ({
      category: String(row[catCol] || '').trim(),
      mainTitle: String(row[mtCol] || '').trim(),
      subTitle: String(row[stCol] || '').trim(),
    }))
    .filter((row) => row.category);
}

// Rebuilds the tree from flat spreadsheet rows, reusing the existing node's
// id (and, for sub titles, its status/hidden/content) whenever the name at
// that level matches something already in the course — so re-uploading an
// unchanged export is a no-op, and only genuinely new/removed rows change.
function reconcileTreeFromRows(rows, existingTree) {
  const tree = [];
  const catByName = new Map();

  rows.forEach(({ category, mainTitle, subTitle }) => {
    let cat = catByName.get(category);
    if (!cat) {
      const existingCat = (existingTree || []).find((c) => c.name === category);
      cat = existingCat ? { id: existingCat.id, name: existingCat.name, mainTitles: [] } : newCategory(category);
      catByName.set(category, cat);
      tree.push(cat);
    }
    if (!mainTitle) return;

    let mt = cat.mainTitles.find((m) => m.title === mainTitle);
    if (!mt) {
      const existingCat = (existingTree || []).find((c) => c.name === category);
      const existingMt = existingCat?.mainTitles.find((m) => m.title === mainTitle);
      mt = existingMt ? { id: existingMt.id, title: existingMt.title, subTitles: [] } : newMainTitle(mainTitle);
      cat.mainTitles.push(mt);
    }
    if (!subTitle) return;

    const existingCat = (existingTree || []).find((c) => c.name === category);
    const existingMt = existingCat?.mainTitles.find((m) => m.title === mainTitle);
    const existingSt = existingMt?.subTitles.find((s) => s.title === subTitle);
    mt.subTitles.push(existingSt ? { ...existingSt } : newSubTitle(subTitle));
  });

  return tree;
}

function filterTreeByQuery(tree, query) {
  const q = query.trim().toLowerCase();
  if (!q) return tree || [];
  return (tree || [])
    .map((cat) => {
      const catMatches = cat.name.toLowerCase().includes(q);
      const mainTitles = (cat.mainTitles || [])
        .map((mt) => {
          const mtMatches = mt.title.toLowerCase().includes(q);
          const subTitles = (mt.subTitles || []).filter(
            (st) => catMatches || mtMatches || st.title.toLowerCase().includes(q)
          );
          return catMatches || mtMatches || subTitles.length > 0 ? { ...mt, subTitles } : null;
        })
        .filter(Boolean);
      return catMatches || mainTitles.length > 0 ? { ...cat, mainTitles } : null;
    })
    .filter(Boolean);
}

function QuickAddExcelView({ course, onBack, onSynced }) {
  const [search, setSearch] = useState('');
  const [file, setFile] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const previewTree = useMemo(() => filterTreeByQuery(course.tree, search), [course.tree, search]);
  const lessonCount = countLessons(course.tree);

  const handleSync = async () => {
    if (!file) return;
    setSyncing(true);
    setError('');
    try {
      const rows = await parseTocFile(file);
      const tree = reconcileTreeFromRows(rows, course.tree);
      const updated = await updateCourse(course.id, { tree });
      onSynced(updated);
    } catch (err) {
      setError(err.message || 'Failed to sync the uploaded file.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-5 py-3 shadow-sm">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Content Editor
        </button>
        <span className="text-slate-300">/</span>
        <h1 className="text-sm font-bold text-slate-800">Quick Add Table of Content</h1>
      </div>

      <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-6 py-8">
        <h2 className="text-lg font-bold text-slate-800">Bulk import via Excel</h2>
        <p className="mt-1 text-sm text-slate-500">
          Download the current table of content as a spreadsheet, edit it, then upload it back.
          Matching names get updated; new names get added.
        </p>

        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Already in this course</span>
            <span className="text-xs text-slate-400">{lessonCount} lesson{lessonCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="relative mb-3">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category, main title, or lesson…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-100">
            {previewTree.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-slate-400">
                {course.tree.length === 0 ? 'No structure yet.' : 'No matches.'}
              </p>
            ) : (
              previewTree.map((cat) => (
                <div key={cat.id} className="px-3 py-1.5">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{cat.name}</p>
                  {cat.mainTitles.map((mt) => (
                    <div key={mt.id} className="mt-1 pl-2">
                      <p className="text-xs font-bold text-slate-700">{mt.title}</p>
                      <ul className="mt-0.5 space-y-0.5 pl-4">
                        {mt.subTitles.map((st) => (
                          <li key={st.id} className="flex items-center gap-1.5 text-xs text-slate-500">
                            <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                            {st.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Step 1</p>
          <p className="mb-3 mt-1 text-sm text-slate-600">
            Start from a blank template, or from everything already entered — both include the Instructions tab.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => downloadTocTemplate(course.tree, { blank: true })}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download Blank Template (.xlsx)
            </button>
            <button
              onClick={() => downloadTocTemplate(course.tree, { blank: false })}
              className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 transition"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download Current Data (.xlsx)
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Step 2</p>
          <p className="mb-3 mt-1 text-sm text-slate-600">Upload the filled-in template.</p>
          <button
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2.5 text-sm text-slate-500 hover:border-violet-400 hover:text-violet-600 transition"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3V15" />
            </svg>
            {file ? file.name : 'Choose a filled .xlsx file…'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <button
            onClick={handleSync}
            disabled={!file || syncing}
            className="mt-3 w-full rounded-lg bg-slate-800 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {syncing ? 'Syncing…' : 'Upload & Sync'}
          </button>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          Uploading replaces the entire table of content — anything left out of the sheet will be removed.
          See the Instructions tab in the template for details.
        </div>
      </div>
    </div>
  );
}

// ── Main editor page ────────────────────────────────────────────────────────
export default function StudyMaterialEditor() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [selected, setSelected] = useState(null); // { catId, mtId, stId } | null = overview
  const [lang, setLang] = useState('en');
  const [saveState, setSaveState] = useState('saved'); // 'saving' | 'saved'
  const [contentDirty, setContentDirty] = useState(false); // lesson content typed but not yet saved
  const saveTimer = useRef(null);
  const richEditorRef = useRef(null);
  // addForm tracks which inline "name + Add" row is currently open:
  // { type: 'category' } | { type: 'mainTitle', catId } | { type: 'subTitle', catId, mtId }
  const [addForm, setAddForm] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCourse(courseId).then((c) => {
      if (cancelled) return;
      if (!c) { setNotFound(true); return; }
      setCourse(c);
    });
    return () => { cancelled = true; };
  }, [courseId]);

  // A freshly-selected lesson always starts clean -- switchSelected already
  // flushes the previous one's edits before getting here.
  useEffect(() => {
    setContentDirty(false);
  }, [selected?.stId]);

  const persist = (patch, { immediate = false, onSaved, skipPersist = false } = {}) => {
    // Apply the patch to local state immediately so rapid successive edits
    // (e.g. add main title, then add sub title before the first request
    // returns) always read an up-to-date tree instead of racing the network.
    setCourse((prev) => (prev ? { ...prev, ...patch } : prev));
    // Lesson content typing uses skipPersist -- it updates local state (so the
    // editor and sidebar stay in sync) but deliberately doesn't touch the
    // server until the Save button (or a lesson switch) flushes it.
    if (skipPersist) return;
    setSaveState('saving');
    const run = async () => {
      const updated = await updateCourse(courseId, patch);
      setCourse(updated);
      setSaveState('saved');
      onSaved?.(updated);
    };
    if (immediate) { run(); return; }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(run, 450);
  };

  const persistTree = (tree, opts) => persist({ tree }, opts);

  const selectedNodes = useMemo(() => {
    if (!course || !selected) return null;
    const cat = course.tree.find((c) => c.id === selected.catId);
    if (!cat) return null;
    const mt = cat.mainTitles.find((m) => m.id === selected.mtId);
    if (!mt) return null;
    const sub = mt.subTitles.find((s) => s.id === selected.stId);
    if (!sub) return null;
    return { cat, mt, sub };
  }, [course, selected]);

  const lessonIndex = useMemo(() => {
    if (!course || !selected) return null;
    let i = 0;
    for (const cat of course.tree) {
      for (const mt of cat.mainTitles) {
        for (const st of mt.subTitles) {
          i += 1;
          if (st.id === selected.stId) return i;
        }
      }
    }
    return null;
  }, [course, selected]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 text-slate-500">
        <p className="text-sm">Course not found.</p>
        <button onClick={() => navigate('/admin/study-materials')} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white">
          Back to Study Materials
        </button>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 text-slate-400">
        <svg className="h-8 w-8 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <p className="text-sm">Loading course…</p>
      </div>
    );
  }

  if (quickAddOpen) {
    return (
      <QuickAddExcelView
        course={course}
        onBack={() => setQuickAddOpen(false)}
        onSynced={(updated) => { setCourse(updated); setQuickAddOpen(false); }}
      />
    );
  }

  const mapTree = (fn) => {
    const tree = course.tree.map((cat) => ({ ...cat, mainTitles: cat.mainTitles.map((mt) => ({ ...mt, subTitles: [...mt.subTitles] })) }));
    fn(tree);
    return tree;
  };

  const submitAddCategory = (name) => {
    const tree = [...course.tree, newCategory(name)];
    persistTree(tree, { immediate: true });
    setAddForm(null);
  };

  const submitAddMainTitle = (catId, name) => {
    const tree = mapTree((t) => {
      const cat = t.find((c) => c.id === catId);
      cat.mainTitles.push(newMainTitle(name));
    });
    persistTree(tree, { immediate: true });
    setAddForm(null);
  };

  const submitAddSubTitle = (catId, mtId, name) => {
    const sub = newSubTitle(name);
    const tree = mapTree((t) => {
      const cat = t.find((c) => c.id === catId);
      const mt = cat.mainTitles.find((m) => m.id === mtId);
      mt.subTitles.push(sub);
    });
    setSelected({ catId, mtId, stId: sub.id });
    setAddForm(null);
    persistTree(tree, {
      immediate: true,
      // The server assigns real ids on insert, so the temp id used for
      // optimistic selection above needs to be swapped for the saved one.
      onSaved: (updated) => {
        const newCat = updated.tree.find((c) => c.id === catId)
          || updated.tree.find((c) => c.mainTitles.some((m) => m.id === mtId));
        const newMt = newCat?.mainTitles.find((m) => m.id === mtId)
          || newCat?.mainTitles[newCat.mainTitles.length - 1];
        const newSub = newMt?.subTitles[newMt.subTitles.length - 1];
        if (newCat && newMt && newSub) setSelected({ catId: newCat.id, mtId: newMt.id, stId: newSub.id });
      },
    });
  };

  const renameCategory = (catId, name) => {
    const tree = mapTree((t) => { t.find((c) => c.id === catId).name = name; });
    persistTree(tree, { immediate: true });
  };

  const renameMainTitle = (catId, mtId, title) => {
    const tree = mapTree((t) => {
      t.find((c) => c.id === catId).mainTitles.find((m) => m.id === mtId).title = title;
    });
    persistTree(tree, { immediate: true });
  };

  const renameSubTitle = (catId, mtId, stId, title) => {
    const tree = mapTree((t) => {
      t.find((c) => c.id === catId).mainTitles.find((m) => m.id === mtId).subTitles.find((s) => s.id === stId).title = title;
    });
    persistTree(tree, { immediate: true });
  };

  const deleteCategory = (catId) => {
    if (!window.confirm('Delete this category and everything inside it?')) return;
    const tree = course.tree.filter((c) => c.id !== catId);
    if (selected?.catId === catId) setSelected(null);
    persistTree(tree, { immediate: true });
  };

  const deleteMainTitle = (catId, mtId) => {
    if (!window.confirm('Delete this main title and its sub titles?')) return;
    const tree = mapTree((t) => {
      const cat = t.find((c) => c.id === catId);
      cat.mainTitles = cat.mainTitles.filter((m) => m.id !== mtId);
    });
    if (selected?.mtId === mtId) setSelected(null);
    persistTree(tree, { immediate: true });
  };

  const deleteSubTitle = (catId, mtId, stId) => {
    if (!window.confirm('Delete this sub title?')) return;
    const tree = mapTree((t) => {
      const mt = t.find((c) => c.id === catId).mainTitles.find((m) => m.id === mtId);
      mt.subTitles = mt.subTitles.filter((s) => s.id !== stId);
    });
    if (selected?.stId === stId) setSelected(null);
    persistTree(tree, { immediate: true });
  };

  const moveMainTitle = (catId, mtId, dir) => {
    const tree = mapTree((t) => {
      const cat = t.find((c) => c.id === catId);
      const idx = cat.mainTitles.findIndex((m) => m.id === mtId);
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= cat.mainTitles.length) return;
      [cat.mainTitles[idx], cat.mainTitles[swapIdx]] = [cat.mainTitles[swapIdx], cat.mainTitles[idx]];
    });
    persistTree(tree, { immediate: true });
  };

  const moveSubTitle = (catId, mtId, stId, dir) => {
    const tree = mapTree((t) => {
      const mt = t.find((c) => c.id === catId).mainTitles.find((m) => m.id === mtId);
      const idx = mt.subTitles.findIndex((s) => s.id === stId);
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= mt.subTitles.length) return;
      [mt.subTitles[idx], mt.subTitles[swapIdx]] = [mt.subTitles[swapIdx], mt.subTitles[idx]];
    });
    persistTree(tree, { immediate: true });
  };

  const patchSelectedSub = (patch, opts) => {
    if (!selected) return;
    const tree = mapTree((t) => {
      const mt = t.find((c) => c.id === selected.catId).mainTitles.find((m) => m.id === selected.mtId);
      const idx = mt.subTitles.findIndex((s) => s.id === selected.stId);
      mt.subTitles[idx] = { ...mt.subTitles[idx], ...patch };
    });
    persistTree(tree, opts);
  };

  // Lesson content is saved explicitly (Save button) rather than as-you-type.
  // saveContent flushes whatever's currently in local state for the selected
  // lesson; switchSelected calls it first so leaving a lesson with unsaved
  // edits doesn't silently drop them.
  const saveContent = (onSaved) => {
    if (!selected || !contentDirty) return;
    const currentSub = selectedNodes?.sub;
    if (!currentSub) return;
    patchSelectedSub(
      { content: currentSub.content },
      { immediate: true, onSaved: () => { setContentDirty(false); onSaved?.(); } }
    );
  };

  const switchSelected = (next) => {
    if (contentDirty) saveContent();
    setSelected(next);
  };

  const publishNow = () => {
    persist({ status: 'published' }, { immediate: true });
  };

  const sub = selectedNodes?.sub;
  const lessonCount = countLessons(course.tree);

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      {/* Top bar */}
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-5 py-3 shadow-sm">
        <button
          onClick={() => { if (contentDirty) saveContent(); navigate('/admin/study-materials'); }}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back
        </button>
        <span className="text-slate-300">/</span>
        <h1 className="text-sm font-bold text-slate-800">Course Content Editor</h1>
        {lessonIndex && (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">#{lessonIndex}</span>
        )}
        <div className="ml-2 flex items-center gap-1">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                lang === l.code ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {l.flag} {l.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3">
          <span className={`flex items-center gap-1.5 text-xs ${contentDirty && saveState !== 'saving' ? 'text-amber-600' : 'text-slate-400'}`}>
            {saveState === 'saving' ? (
              <>
                <svg className="h-3.5 w-3.5 animate-spin text-violet-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
                </svg>
                Saving…
              </>
            ) : contentDirty ? (
              <>
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                Unsaved changes — click Save
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143z" clipRule="evenodd" />
                </svg>
                All changes saved
              </>
            )}
          </span>
          {selected && (
            <button
              type="button"
              onClick={() => saveContent()}
              disabled={!contentDirty}
              className="rounded-lg bg-violet-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
            >
              Save
            </button>
          )}
          <button
            onClick={publishNow}
            className="rounded-lg bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 transition shadow-sm"
          >
            {course.status === 'published' ? 'Published' : 'Publish now'}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar */}
        <div className="flex w-80 shrink-0 flex-col border-r border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Structure</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuickAddOpen(true)}
                className="flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-800 transition"
                title="Bulk add via Excel"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
                </svg>
                Quick Add
              </button>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <span>Edit</span>
                <button
                  type="button"
                  onClick={() => setEditMode((v) => !v)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${editMode ? 'bg-violet-600' : 'bg-slate-200'}`}
                >
                  <span className={`inline-block h-4 w-4 shrink-0 rounded-full bg-white shadow transition-transform ${editMode ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            <button
              onClick={() => switchSelected(null)}
              className={`mb-1 w-full rounded-lg px-3 py-2 text-left text-xs font-semibold transition ${
                !selected ? 'bg-violet-100 text-violet-800' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              Course Overview
            </button>

            {course.tree.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-slate-400">No structure yet. Add a category to get started.</p>
            )}

            {course.tree.map((cat) => (
              <div key={cat.id} className="mb-1">
                <div className="group flex items-center gap-1 rounded-lg px-2 py-1.5">
                  <span className="flex-1 truncate text-xs font-bold text-slate-800">{cat.name}</span>
                  {editMode && (
                    <div className="hidden items-center gap-0.5 group-hover:flex">
                      <button onClick={() => setAddForm({ type: 'mainTitle', catId: cat.id })} className="rounded p-0.5 text-slate-300 hover:text-violet-600" title="Add main title">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                      </button>
                      <button onClick={() => { const n = prompt('Rename category:', cat.name); if (n?.trim()) renameCategory(cat.id, n.trim()); }} className="rounded p-0.5 text-slate-300 hover:text-violet-600" title="Rename">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 18.82 2.25 20.25l1.43-4.582 13.182-13.181z" /></svg>
                      </button>
                      <button onClick={() => deleteCategory(cat.id)} className="rounded p-0.5 text-slate-300 hover:text-red-500" title="Delete">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )}
                </div>

                {cat.mainTitles.map((mt, mtIdx) => (
                  <div key={mt.id}>
                    <div className="group flex items-center gap-1 rounded-lg py-1.5 pl-4 pr-2">
                      <span className="flex-1 truncate text-xs font-semibold text-slate-600">{mt.title}</span>
                      {editMode && (
                        <div className="hidden items-center gap-0.5 group-hover:flex">
                          <button onClick={() => moveMainTitle(cat.id, mt.id, -1)} disabled={mtIdx === 0} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" title="Move up">
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg>
                          </button>
                          <button onClick={() => moveMainTitle(cat.id, mt.id, 1)} disabled={mtIdx === cat.mainTitles.length - 1} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" title="Move down">
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                          </button>
                          <button onClick={() => setAddForm({ type: 'subTitle', catId: cat.id, mtId: mt.id })} className="rounded p-0.5 text-slate-300 hover:text-violet-600" title="Add sub title">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                          </button>
                          <button onClick={() => { const n = prompt('Rename main title:', mt.title); if (n?.trim()) renameMainTitle(cat.id, mt.id, n.trim()); }} className="rounded p-0.5 text-slate-300 hover:text-violet-600" title="Rename">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 18.82 2.25 20.25l1.43-4.582 13.182-13.181z" /></svg>
                          </button>
                          <button onClick={() => deleteMainTitle(cat.id, mt.id)} className="rounded p-0.5 text-slate-300 hover:text-red-500" title="Delete">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {mt.subTitles.map((st, stIdx) => (
                      <TreeSubTitle
                        key={st.id}
                        sub={st}
                        editMode={editMode}
                        selected={selected?.stId === st.id}
                        isFirst={stIdx === 0}
                        isLast={stIdx === mt.subTitles.length - 1}
                        onSelect={() => switchSelected({ catId: cat.id, mtId: mt.id, stId: st.id })}
                        onRename={(title) => renameSubTitle(cat.id, mt.id, st.id, title)}
                        onDelete={() => deleteSubTitle(cat.id, mt.id, st.id)}
                        onMove={(dir) => moveSubTitle(cat.id, mt.id, st.id, dir)}
                      />
                    ))}

                    {editMode && addForm?.type === 'subTitle' && addForm.catId === cat.id && addForm.mtId === mt.id ? (
                      <AddRow
                        indentClass="pl-8"
                        placeholder="Sub title…"
                        onAdd={(name) => submitAddSubTitle(cat.id, mt.id, name)}
                        onCancel={() => setAddForm(null)}
                      />
                    ) : editMode && mt.subTitles.length === 0 && (
                      <button onClick={() => setAddForm({ type: 'subTitle', catId: cat.id, mtId: mt.id })} className="mb-1 ml-8 flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] text-violet-500 hover:bg-violet-50">
                        + Add sub title
                      </button>
                    )}
                  </div>
                ))}

                {editMode && (
                  addForm?.type === 'mainTitle' && addForm.catId === cat.id ? (
                    <AddRow
                      indentClass="pl-4"
                      placeholder="Main title…"
                      onAdd={(name) => submitAddMainTitle(cat.id, name)}
                      onCancel={() => setAddForm(null)}
                    />
                  ) : (
                    <button onClick={() => setAddForm({ type: 'mainTitle', catId: cat.id })} className="ml-4 flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] text-slate-400 hover:bg-slate-50 hover:text-violet-600">
                      + Add main title
                    </button>
                  )
                )}
              </div>
            ))}

            {editMode && (
              addForm?.type === 'category' ? (
                <AddRow
                  placeholder="Category…"
                  onAdd={submitAddCategory}
                  onCancel={() => setAddForm(null)}
                />
              ) : (
                <button
                  onClick={() => setAddForm({ type: 'category' })}
                  title="Add category"
                  className="mt-1 flex h-7 w-7 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400 transition hover:border-violet-400 hover:text-violet-600"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                </button>
              )
            )}
          </div>

          <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
            {lessonCount} lesson{lessonCount !== 1 ? 's' : ''} total
          </div>
        </div>

        {/* Main content */}
        <div className="min-w-0 flex-1 overflow-y-auto">
          {!sub ? (
            <div className="mx-auto max-w-2xl px-6 py-8">
              <h2 className="mb-4 text-sm font-bold text-slate-700">Course Overview</h2>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Title</label>
                  <input
                    defaultValue={course.title}
                    onChange={(e) => persist({ title: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Description</label>
                  <textarea
                    defaultValue={course.description}
                    onChange={(e) => persist({ description: e.target.value })}
                    rows={4}
                    className="w-full resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                  />
                </div>
              </div>
              {course.tree.length === 0 && (
                <div className="mt-4 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-12 text-slate-400">
                  <p className="text-sm">Start building this course's table of content from the sidebar.</p>
                  <button onClick={() => { setEditMode(true); setAddForm({ type: 'category' }); }} className="mt-3 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 transition">
                    Add first category
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full px-6 py-8">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
                <span>{selectedNodes.cat.name}</span>
                <span>/</span>
                <span>{selectedNodes.mt.title}</span>
              </div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-bold text-slate-800">{sub.title}</h2>
                <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[sub.status] || STATUS_BADGE.draft}`}>
                  {sub.status || 'draft'}
                </span>
                {sub.updatedAt && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143z" clipRule="evenodd" />
                    </svg>
                    Saved {fmtSavedAt(sub.updatedAt)}
                  </span>
                )}
              </div>

              <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
                  {LESSON_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => patchSelectedSub({ status: s }, { immediate: true })}
                      className={`rounded-md px-3 py-1 text-xs font-semibold capitalize transition ${
                        sub.status === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${STATUS_DOT[s]}`} />
                      {s}
                    </button>
                  ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Hidden</span>
                  <button
                    type="button"
                    onClick={() => patchSelectedSub({ hidden: !sub.hidden }, { immediate: true })}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${sub.hidden ? 'bg-slate-700' : 'bg-slate-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 shrink-0 rounded-full bg-white shadow transition-transform ${sub.hidden ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Lesson Content</h3>
                  <p className="text-xs text-slate-400">Use the toolbar below to write and format the content for this lesson ({LANGUAGES.find((l) => l.code === lang)?.label}).</p>
                </div>
                {Boolean(sub.content?.[lang]) && (
                  <button
                    type="button"
                    onClick={async () => {
                      const result = await Swal.fire({
                        title: 'Clear this content?',
                        text: `All ${LANGUAGES.find((l) => l.code === lang)?.label} content for this lesson will be removed.`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#dc2626',
                        cancelButtonColor: '#6b7280',
                        confirmButtonText: 'Yes, clear it',
                        cancelButtonText: 'Cancel',
                      });
                      if (!result.isConfirmed) return;
                      richEditorRef.current?.clear();
                      patchSelectedSub({ content: { ...sub.content, [lang]: '' } }, { skipPersist: true });
                      setContentDirty(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                    Clear
                  </button>
                )}
              </div>

              <RichEditor
                ref={richEditorRef}
                editorKey={`${sub.id}-${lang}`}
                defaultValue={sub.content?.[lang] || ''}
                placeholder={`${LANGUAGES.find((l) => l.code === lang)?.label} content here…`}
                onChange={(html) => {
                  patchSelectedSub({ content: { ...sub.content, [lang]: html } }, { skipPersist: true });
                  setContentDirty(true);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
